import { useEffect, useMemo, useState } from "react";

import CustomGrid from "../CustomGrid";
import { gridStructure } from "constants/CustomGridStructure";
import { GridRowSelectionModel } from "@mui/x-data-grid";

import { Box, Paper } from "@mui/material";
import Loader from "../Loader";

import AddLocationModal from "./AddLocationModal";
import EditLocationModal from "./EditLocationModal";
import DeleteConfirmationModaL from "components/utils/DeleteConfirmationModal";
import { EnhancedTableToolbar } from "../EnhancedToolbar";

import { ApiErrorData, Location, SubLocation } from "types";
import { LOCATIONS_URL as BASE_URL } from "constants/urls";
import { ENTITY } from "constants/entities";

import useModals from "../../../hooks/useModals";
import useCrud from "../../../hooks/useCrud";
import { useToast } from "hooks/useToast";
import { STATUS_CODES } from "constants/index";
import { useSession } from "providers/SessionProvider";
import { useFilter } from "hooks/useFilter";
import { getUserFullName } from "helpers";
import { exportToExcel } from "helpers/exportToExcel";

export default function LocationsList() {
  const [selected, setSelected] = useState<GridRowSelectionModel>([]);

  const {
    locations,
    sublocations,
    stores,
    addSessionData,
    updateSessionData,
    deleteSessionData,
    users,
  } = useSession();
  const { showSnackBar } = useToast();
  const {
    addModalOpen,
    setAddModalOpen,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
  } = useModals();

  const {
    loading,
    firstLoad,
    updateData: updateLocation,
    deleteData: deleteLocation,
    createData: createLocation,
    showToast,
    toastMessage,
    toastSeverity,
    setShowToast,
  } = useCrud<Location>(BASE_URL);

  const locationsExcelColumns = {
    "Código": (l: Location & { store_code?: string }) => l.code ?? "-",
    "Almacén": (l: Location & { store_code?: string }) =>
      l.store_code ?? l.store?.code ?? "-",
    "Tipo": (l: Location & { store_code?: string }) => l.location_type ?? "-",
    "Oculta para clientes": (l: Location & { store_code?: string }) =>
      l.is_hidden ? "Sí" : "No",
    "Ancho": (l: Location & { store_code?: string }) => l.width ?? 0,
    "Alto": (l: Location & { store_code?: string }) => l.height ?? 0,
    "Largo": (l: Location & { store_code?: string }) => l.long ?? 0,
    "Creado por": (l: Location & { created_by_name?: string }) =>
      l.created_by_name ?? "-",
  } satisfies Record<string, (row: Location & { store_code?: string }) => any>;

  const enrichedLocations = useMemo(() => {
    return locations
      .filter((l) => l.parent_location_id == null)
      .map((item) => ({
        ...item,
        store_code: item.store.code,
        created_by_name: (item as Location & { created_by?: number | null })
          .created_by
          ? getUserFullName(
              (item as Location & { created_by?: number | null }).created_by,
              users,
            )
          : "SISTEMA",
      }));
  }, [locations, users]);

  const { filterText, setFilterText, filteredData: filteredLocations } =
    useFilter<Location & { store_code: string; created_by_name: string }>({
      data: enrichedLocations,
      fields: gridStructure[ENTITY.LOCATIONS].fields,
    });

  const syncParentAndSubLocations = (
    updatedParent: Location & { sub_locations?: SubLocation[] }
  ) => {
    const parentId = updatedParent.id;
    const activeSubLocations = updatedParent.sub_locations ?? [];

    updateSessionData(ENTITY.LOCATIONS, updatedParent);

    const cachedSubLocations = locations.filter(
      (loc) => loc.parent_location_id === parentId
    );

    if (cachedSubLocations.length > 0) {
      const cachedSubLocationIds = cachedSubLocations.map((loc) =>
        String(loc.id)
      );

      deleteSessionData(ENTITY.SUBLOCATIONS, cachedSubLocationIds);
      deleteSessionData(ENTITY.LOCATIONS, cachedSubLocationIds);
    }

    activeSubLocations.forEach((sub) => {
      addSessionData(ENTITY.SUBLOCATIONS, sub);
      addSessionData(ENTITY.LOCATIONS, sub);
    });
  };

  const handleExportLocationsExcel = () => {
    exportToExcel<Location & { store_code?: string }>({
      data: filteredLocations,
      columns: locationsExcelColumns,
      sheetName: "Ubicaciones",
      fileNamePrefix: "Ubicaciones",
      headerRows: [
        ["Listado de Ubicaciones"],
        [`Filtro: ${filterText?.trim() ? filterText : "—"}`],
        [],
      ],
    });
  };

  const handleDeleteSelected = async () => {
    const failedDeletes: number[] = [];

    for (const id of selected) {
      const reponse = await deleteLocation(id, true, true);
      if (reponse.status !== STATUS_CODES.OK) {
        failedDeletes.push(Number(id));
      }
    }
    deleteSessionData(
      ENTITY.LOCATIONS,
      selected
        .filter((item) => !failedDeletes.includes(Number(item)))
        .map((item) => item.toString()),
    );

    setSelected([]);
  };

  const handleCreate = async (newLocation: Location) => {
    const response = await createLocation(newLocation, true, true);
    if (response.status === STATUS_CODES.OK) {
      addSessionData(ENTITY.LOCATIONS, response.data as Location);
    }
  };

  const handleEdit = async (locationToEdit: Location) => {
    try {
      const response = await updateLocation(
        locationToEdit.id,
        locationToEdit,
        true,
        false,
      );

      if (response.status === STATUS_CODES.OK) {
        const data = response.data as Location & { sub_locations?: SubLocation[] };

        syncParentAndSubLocations(data);

        setSelected([]);
        showSnackBar("Ubicación actualizada con éxito", "success");
        return data;
      }

      const data = (response.data ?? {}) as ApiErrorData;
      const msg = data.message ?? "Error al actualizar ubicación";
      showSnackBar(msg, "error");
      return null;
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Error inesperado";
      showSnackBar(msg, "error");
      return null;
    }
  };

  useEffect(() => {
    if (showToast) {
      showSnackBar(toastMessage, toastSeverity);
      setShowToast(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showToast]);

  return (
    <>
      {firstLoad ? (
        <Loader isFirstLoad={firstLoad} isLoading={loading} />
      ) : (
        <>
          <Loader isFirstLoad={firstLoad} isLoading={loading} />
          <AddLocationModal
            open={addModalOpen}
            handleOpen={() => setAddModalOpen(!addModalOpen)}
            onCreate={handleCreate}
            stores={stores}
          />
          <DeleteConfirmationModaL
            open={deleteModalOpen}
            handleOpen={() => setDeleteModalOpen(!deleteModalOpen)}
            entityName={ENTITY.LOCATIONS}
            values={selected.map((item) => {
              const value = locations.find((l) => l.id === item)?.code;
              if (value) return value;
              return "";
            })}
            handleDelete={handleDeleteSelected}
          />
          {selected.length > 0 ? (
            <EditLocationModal
              open={editModalOpen}
              handleOpen={() => setEditModalOpen(!editModalOpen)}
              value={locations.find((l) => l.id === selected[0]) as Location}
              onEdit={handleEdit}
            />
          ) : null}
          <Box sx={{ width: "99%" }}>
            <Paper sx={{ width: "100%", mb: 2 }}>
              <EnhancedTableToolbar
                numSelected={selected.length}
                handleAddModalOpen={() => setAddModalOpen(!addModalOpen)}
                handleEditModalOpen={() => setEditModalOpen(!editModalOpen)}
                handleDeleteModalOpen={() =>
                  setDeleteModalOpen(!deleteModalOpen)
                }
                onFilterChange={setFilterText}
                onExportExcel={handleExportLocationsExcel}
              />
              <CustomGrid
                structure={gridStructure[ENTITY.LOCATIONS]}
                rows={filteredLocations}
                onEdit={handleEdit}
                selectionModel={selected}
                onRowSelection={setSelected}
              />
            </Paper>
          </Box>
        </>
      )}
    </>
  );
}
